import React, { useState, useEffect } from "react";
import { Form } from "react-bootstrap";
import { useNavigate, useLocation } from "react-router-dom";
import toast from "react-hot-toast";
import { useButtonTableStyle } from "../utils/useButtonTableStyle";
import PermissionChecklist from "../components/permissions/PermissionChecklist";
import {
  getRole,
  updateRole,
  isErpApiSuccess,
  listAdminUserPermissions,
} from "../erpClient";

function EditRole() {
  const navigate = useNavigate();
  const { state } = useLocation();
  const { buttonClass, cardClass } = useButtonTableStyle();

  const roleId = state?.roleId ?? state?.role?.id;
  const [roleName, setRoleName] = useState(state?.role?.name ?? "");
  const [permissionCodes, setPermissionCodes] = useState(
    Array.isArray(state?.role?.permission_codes)
      ? state.role.permission_codes
      : [],
  );
  const [catalogCodes, setCatalogCodes] = useState([]);
  const [loading, setLoading] = useState(!!roleId);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await listAdminUserPermissions();
        if (cancelled) return;
        const codes = Array.isArray(res?.data?.permission_codes)
          ? res.data.permission_codes.map((c) => String(c).toUpperCase())
          : [];
        setCatalogCodes(codes);
      } catch {
        // ignore
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!roleId) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const res = await getRole({ id: Number(roleId) });
        if (cancelled) return;
        if (isErpApiSuccess(res) && res?.data?.role) {
          const role = res.data.role;
          setRoleName(role.name || "");
          setPermissionCodes(
            Array.isArray(role.permission_codes) ? role.permission_codes : [],
          );
        } else {
          toast.error(res?.message || "Failed to load role");
          navigate("/roletable");
        }
      } catch (err) {
        if (!cancelled) {
          toast.error(
            err?.response?.data?.message ||
              err?.message ||
              "Failed to load role",
          );
          navigate("/roletable");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [roleId, navigate]);

  if (!roleId) {
    return (
      <div className="body-wrapper">
        <div className="container-fluid">
          <div className={`card w-100 p-5 text-center ${cardClass}`}>
            <p className="text-muted mb-3">No role selected.</p>
            <button
              className={`btn btn-primary ${buttonClass}`}
              onClick={() => navigate("/roletable")}
            >
              Back to Role Management
            </button>
          </div>
        </div>
      </div>
    );
  }

  const handleSave = async (e) => {
    e.preventDefault();
    if (!roleName.trim()) {
      toast.error("Role name is required");
      return;
    }
    setSaving(true);
    try {
      const res = await updateRole({
        id: Number(roleId),
        name: roleName.trim(),
        permission_codes: permissionCodes,
      });
      if (isErpApiSuccess(res)) {
        toast.success(res?.message || `Role "${roleName.trim()}" updated`);
        navigate("/roletable");
      } else {
        toast.error(res?.message || "Failed to update role");
      }
    } catch (err) {
      toast.error(
        err?.response?.data?.message ||
          err?.message ||
          "Failed to update role",
      );
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="body-wrapper">
        <div className="container-fluid">
          <div className={`card ${cardClass}`}>
            <div className="card-body text-center py-5">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
              <p className="mt-2 mb-0">Loading role...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="body-wrapper">
      <div className="container-fluid">
        <div
          className={`card w-100 position-relative overflow-hidden ${cardClass}`}
        >
          <div className="px-4 py-3 border-bottom d-flex justify-content-between align-items-center">
            <h4 className="card-title mb-0">Edit Role</h4>
          </div>

          <div className="card-body p-4">
            <form onSubmit={handleSave}>
              <div className="row g-4 mb-4">
                <div className="col-md-4">
                  <Form.Label className="fw-semibold">
                    Role Name <span className="text-danger">*</span>
                  </Form.Label>
                  <Form.Control
                    id="edit-role-name"
                    type="text"
                    className={buttonClass}
                    placeholder="e.g. Sales Manager"
                    value={roleName}
                    onChange={(e) => setRoleName(e.target.value)}
                    required
                  />
                </div>
              </div>

              <hr className="mb-4" />
              <h5 className="fw-semibold mb-3">Permissions</h5>
              <PermissionChecklist
                value={permissionCodes}
                onChange={setPermissionCodes}
                catalogCodes={catalogCodes}
              />

              <div className="d-flex justify-content-end mt-4 gap-2">
                <button
                  type="button"
                  className={`btn btn-secondary ${buttonClass}`}
                  onClick={() => navigate("/roletable")}
                  disabled={saving}
                >
                  Cancel
                </button>
                <button
                  id="btn-update-role"
                  type="submit"
                  className={`btn btn-primary ${buttonClass}`}
                  disabled={saving}
                >
                  {saving ? "Updating…" : "Update Role"}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}

export default EditRole;
